import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Link,
  IconButton,
  Badge,
  Tooltip,
} from "@mui/material";
import GenericTable from "../../components/table";
import {
  FilterSelect,
  FilterDate,
  ClearFilters,
} from "../../components/ui/FilterBar";
import ExpenseDetailsDialog from "./ExpenseDetailsDialog";
import FilterListIcon from "@mui/icons-material/FilterListOutlined";
import { ExpenseApi, ProjectApi, UserApi } from "../../apis";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  EXPENSE_CATEGORIES,
  canModifyExpense,
  getExpenseBasePath,
  isAdminUser,
  loadSelectableProjects,
} from "./helpers";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { isPdfReceipt } from "../../utils/receipts";

const emptyFilters = {
  projectId: "",
  phaseId: "",
  category: "",
  startDate: "",
  endDate: "",
};

function Expenses() {
  const navigate = useNavigate();
  const isAdmin = isAdminUser();
  const basePath = getExpenseBasePath();

  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ExpenseApi.getAllExpenses(filters);
      const list = res?.data?.data?.expenses || [];

      const mapped = list.map((expense) => ({
        id: expense.id,
        date: expense.date ? new Date(expense.date).toLocaleDateString() : "",
        projectName: expense.project?.name || "",
        phaseName: expense.phase?.name || "-",
        category: expense.category,
        amount: Number(expense.amount || 0).toFixed(2),
        // Not "userName": GenericTable reserves that field and renders the
        // logged-in user's name instead of the row value.
        submittedBy: expense.user?.name || expense.user?.email || "",
        notes: expense.notes || "",
        receiptUrl: expense.receiptUrl,
        userId: expense.userId,
        createdAt: expense.createdAt,
      }));

      setExpenses(mapped);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjects(await loadSelectableProjects({ ProjectApi, UserApi }));
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      }
    };

    loadProjects();
  }, []);

  const phaseOptions = useMemo(() => {
    const selected = projects.find(
      (project) => String(project.id) === String(filters.projectId)
    );
    return selected?.phases || [];
  }, [projects, filters.projectId]);

  const total = useMemo(
    () => expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
    [expenses]
  );

  const handleFilterChange = (field) => (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      // A phase only makes sense within its project.
      ...(field === "projectId" ? { phaseId: "" } : {}),
    }));
  };

  const resolveExpense = useCallback(
    (rowOrId) =>
      rowOrId && typeof rowOrId === "object"
        ? rowOrId
        : expenses.find((e) => String(e.id) === String(rowOrId)),
    [expenses]
  );

  const handleEdit = useCallback(
    (rowOrId) => {
      const row = resolveExpense(rowOrId);
      if (!row) return;
      if (!canModifyExpense(row)) {
        toast.error("Expenses can only be edited within 24 hours of creation");
        return;
      }
      navigate(`${basePath}/expenses/${row.id}/edit`);
    },
    [resolveExpense, navigate, basePath]
  );

  const handleDelete = useCallback(
    async (rowOrId) => {
      const row = resolveExpense(rowOrId);
      if (!row) return;
      if (!canModifyExpense(row)) {
        toast.error("Expenses can only be deleted within 24 hours of creation");
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to delete this ${row.category} expense of $${row.amount}?

This action cannot be undone.`
      );
      if (!confirmed) return;

      try {
        await ExpenseApi.deleteExpense(row.id);
        setSelected(null);
        fetchExpenses();
      } catch (error) {
        console.error("Error deleting expense:", error);
        toast.error(
          error.response?.data?.message || "Failed to delete expense. Please try again."
        );
      }
    },
    [resolveExpense, fetchExpenses]
  );

  const columns = useMemo(() => {
    const base = [
      { field: "date", headerName: "Date", sortable: true },
      { field: "projectName", headerName: "Project", sortable: true },
      { field: "phaseName", headerName: "Phase", sortable: true },
      { field: "category", headerName: "Category", sortable: true },
      { field: "amount", headerName: "Amount", sortable: true },
    ];

    if (isAdmin) {
      base.push({ field: "submittedBy", headerName: "User", sortable: true });
    }

    base.push({
      field: "receiptUrl",
      headerName: "Receipt",
      sortable: false,
      renderCell: ({ value }) =>
        value ? (
          <Link href={value} target="_blank" rel="noopener noreferrer">
            {isPdfReceipt(value) ? (
              // No thumbnail to show for a PDF — an <img> here would render
              // as a broken image.
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.subtle",
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 24, color: "error.main" }} />
              </Box>
            ) : (
              <Box
                component="img"
                src={value}
                alt="Receipt"
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            )}
          </Link>
        ) : (
          "-"
        ),
    });

    return base;
  }, [isAdmin]);

  // Counts only what the user actually applied, so "Clear" can stay hidden
  // while the bar is at rest.
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const customFilters = (
    <>
      {/* Five controls made the toolbar a wall by default. They live behind a
          toggle now; the badge keeps applied filters visible while hidden. */}
      <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
        <IconButton
          onClick={() => setShowFilters((v) => !v)}
          aria-label={showFilters ? "Hide filters" : "Show filters"}
          sx={{
            height: 38,
            width: 38,
            borderRadius: 2,
            border: 1,
            borderColor: activeFilterCount ? "primary.main" : "border.default",
            color: activeFilterCount ? "primary.main" : "text.secondary",
          }}
        >
          <Badge badgeContent={activeFilterCount} color="primary">
            <FilterListIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      {showFilters && (
        <>
      <FilterSelect
        label="Project"
        value={filters.projectId}
        onChange={handleFilterChange("projectId")}
        options={projects.map((p) => ({ value: String(p.id), label: p.name }))}
        allLabel="All projects"
        minWidth={170}
      />
      <FilterSelect
        label="Phase"
        value={filters.phaseId}
        onChange={handleFilterChange("phaseId")}
        options={phaseOptions.map((p) => ({ value: String(p.id), label: p.name }))}
        allLabel="All phases"
        disabled={!filters.projectId}
      />
      <FilterSelect
        label="Category"
        value={filters.category}
        onChange={handleFilterChange("category")}
        options={EXPENSE_CATEGORIES.map((c) => ({ value: c, label: c }))}
        allLabel="All categories"
      />
      <FilterDate
        label="From"
        value={filters.startDate}
        onChange={handleFilterChange("startDate")}
      />
      <FilterDate
        label="To"
        value={filters.endDate}
        onChange={handleFilterChange("endDate")}
      />
      <ClearFilters
        count={activeFilterCount}
        onClick={() => setFilters(emptyFilters)}
      />
        </>
      )}
    </>
  );

  return (
    <Box>
      <Grid container spacing={2} mt={2} mb={2}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Typography variant="h4" fontWeight={600}>
            {isAdmin ? "All Expenses" : "My Expenses"}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "#6b6b6b" }}>
            {expenses.length} expense{expenses.length === 1 ? "" : "s"} · Total $
            {total.toFixed(2)}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          lg={6}
          xl={6}
          display="flex"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`${basePath}/expenses/add`)}
          >
            Add Expense
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <GenericTable
          data={expenses}
          columns={columns}
          showAction={true}
          isSearchShown={true}
          inlineEdit={false}
          customFilters={customFilters}
          onRowClick={(row) => setSelected(row)}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <ExpenseDetailsDialog
        expense={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canModify={Boolean(selected && canModifyExpense(selected))}
        isAdmin={isAdmin}
      />
    </Box>
  );
}

export default Expenses;
